import type { GetIndicatorByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Indicator_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { defaultSchedule } from 'src/pages/controls/update/forms/scheduleSchema';
import { getContributors, getOwners } from 'src/rbac/contributorHelper';

import type { IndicatorFormDataFields } from './indicatorSchema';

type SourceIndicator = GetIndicatorByIdQuery['indicator'][number];

/**
 * Builds create-form default values from a source Indicator so the Add
 * Indicator modal opens pre-populated — every field except a fresh name.
 *
 * Excluded on copy:
 * - Title — blank; the user names the new Indicator.
 * - files — attachments are not carried over.
 * - Id + audit metadata — omitted so the save takes the insert (create) branch,
 *   keeping the new record independent of the source.
 * - schedule Start Date + Manual Due Date — left blank like a normal create;
 *   copying a past start date would create an immediately-overdue schedule. The
 *   cadence (Frequency + Time to Complete) is still copied.
 * - Viewers — read-only visibility; redundant to copy.
 * - ancestorContributors — derived from the parent, so recomputed for the new
 *   record.
 *
 * The result is deep-cloned so edits in the open modal never mutate the cached
 * source; the modal holds a snapshot taken when it opened.
 */
export const buildCopiedIndicatorDefaults = (
  source: SourceIndicator
): IndicatorFormDataFields => {
  const shared = {
    Title: '',
    Description: source.Description ?? '',
    Unit: source.Unit ?? '',
    Owners: getOwners(source),
    Contributors: getContributors(source),
    Viewers: [],
    ancestorContributors: [],
    tags: source.tags?.map((tag) => ({ TagTypeId: tag.TagTypeId })) ?? [],
    departments:
      source.departments?.map((department) => ({
        DepartmentTypeId: department.DepartmentTypeId,
      })) ?? [],
    CustomAttributeData: source.CustomAttributeData ?? null,
    files: [],
    schedule: {
      ...defaultSchedule,
      // Copy the source cadence where present; otherwise fall back to the same
      // defaults a blank create uses, so copying an unscheduled source doesn't
      // leave the Time to Complete inputs blank.
      Frequency: source.schedule?.Frequency ?? defaultSchedule.Frequency,
      TimeToCompleteValue:
        source.schedule?.TimeToCompleteValue ??
        defaultSchedule.TimeToCompleteValue,
      TimeToCompleteUnit:
        source.schedule?.TimeToCompleteUnit ??
        defaultSchedule.TimeToCompleteUnit,
      // Deliberately not copied — a past start date would create an
      // immediately-overdue schedule.
      StartDate: null,
      ManualDueDate: null,
    },
  };

  if (source.Type === Indicator_Type_Enum.Text) {
    return structuredClone({
      ...shared,
      Type: Indicator_Type_Enum.Text,
      TargetValueTxt: source.TargetValueTxt ?? '',
    });
  }

  if (source.Type === Indicator_Type_Enum.Boolean) {
    return structuredClone({
      ...shared,
      Type: Indicator_Type_Enum.Boolean,
    });
  }

  return structuredClone({
    ...shared,
    Type: Indicator_Type_Enum.Number,
    LowerToleranceNum: source.LowerToleranceNum ?? null,
    UpperToleranceNum: source.UpperToleranceNum ?? null,
    LowerAppetiteNum: source.LowerAppetiteNum ?? null,
    UpperAppetiteNum: source.UpperAppetiteNum ?? null,
  });
};
